import { useCallback, useEffect, useRef, useState } from 'react';
import { attachFlag, autoLabel, dismissFlag, loadFlagsContext, promoteFlag } from '../../app/flags';
import type { FlagsContext } from '../../app/flags';
import { newId } from '../../app/ids';
import { log } from '../../app/logger';
import { candidateFromFreeText, candidateFromTemplateEntry } from '../../domain/setup';
import type { HypothesisPhase, TemplateEntry } from '../../types';

/**
 * FLAG-3's controller (build unit U-13c).
 *
 * Its own hook rather than four more fields on `useHalftime`, for the reason `§7.6` gives about the
 * hub: halftime's activities are **peers**, and a controller that held all six would make every
 * sheet re-render on every other sheet's write. It is also the shape the independence rule wants —
 * this hook loads and writes flags and can see nothing else's completion state.
 *
 * ── Every write is followed by a re-read of the derived queue ───────────────
 *
 * DIST-1's *"a resolved flag MUST NOT reappear"* is enforced by `listUnresolvedFlags` subtracting
 * resolutions from flags. Removing the row locally instead would be a **second** representation of
 * "resolved" — the one thing DIST-1's append-only design exists to avoid — and the two would
 * disagree the first time a write failed.
 */

const MODULE = 'ui/flags';

export type FlagsPhase = 'loading' | 'ready' | 'failed';

export interface FlagsState {
  readonly phase: FlagsPhase;
  readonly context: FlagsContext | null;
  readonly notice: string | null;
  /**
   * The hypothesis a promotion just created or attached to, held so §7.4's *"optionally a board
   * position"* is one more tap away rather than a hunt through the HT-2 sheet.
   *
   * Cleared when the user leaves the promote flow, so the offer never outlives the moment it belongs
   * to — an offer to place "the pattern you just promoted" three flags later is an offer to place
   * the wrong one (`capture-spawning-instance-handle-at-creation`).
   */
  readonly justPromoted: { readonly id: string; readonly text: string } | null;
  readonly busy: boolean;
}

export interface FlagsActions {
  readonly reload: () => void;
  readonly dismiss: (observationId: string) => Promise<boolean>;
  readonly attach: (
    observationId: string,
    hypothesisId: string,
    text: string,
  ) => Promise<boolean>;
  readonly promoteFromTemplate: (
    observationId: string,
    templateId: string,
    entry: TemplateEntry,
  ) => Promise<boolean>;
  readonly promoteWithAutoLabel: (
    observationId: string,
    matchMinute: number,
    phase: HypothesisPhase,
  ) => Promise<boolean>;
  readonly clearPromoted: () => void;
  readonly dismissNotice: () => void;
}

export function useFlags(matchId: string) {
  const [context, setContext] = useState<FlagsContext | null>(null);
  const [phase, setPhase] = useState<FlagsPhase>('loading');
  const [notice, setNotice] = useState<string | null>(null);
  const [justPromoted, setJustPromoted] = useState<FlagsState['justPromoted']>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Last request wins, never last response — the same guard `useHalftime` carries and for the same
   * hazard: resolving two flags in quick succession puts two reads in flight and nothing orders
   * their resolutions, so the older one landing last would re-render a queue still holding a flag
   * the user has already dealt with. On this sheet that is worse than stale: the user would tap it
   * again and be told it was already resolved.
   */
  const request = useRef(0);
  const mounted = useRef(true);

  const refresh = useCallback(async (): Promise<void> => {
    const ticket = (request.current += 1);
    const next = await loadFlagsContext(matchId);
    if (ticket !== request.current || !mounted.current) return;
    setContext(next);
    setPhase('ready');
  }, [matchId]);

  useEffect(() => {
    mounted.current = true;
    void refresh().catch((cause: unknown) => {
      log.error(MODULE, 'FLAG-3: the flags could not be loaded', {
        matchId,
        message: cause instanceof Error ? cause.message : String(cause),
      });
      if (mounted.current) setPhase('failed');
    });
    return () => {
      mounted.current = false;
    };
  }, [matchId, refresh]);

  const reload = useCallback((): void => {
    void refresh().catch((cause: unknown) => {
      log.error(MODULE, 'FLAG-3: the flags could not be reloaded', {
        matchId,
        message: cause instanceof Error ? cause.message : String(cause),
      });
    });
  }, [matchId, refresh]);

  /**
   * One place every resolution goes through, so the queue re-read cannot be forgotten on one path.
   *
   * `busy` has one owner in the `finally`, so a rejection releases the controls rather than wedging
   * the sheet — the same shape `useHalftime.run` uses, and the notice is cleared first so a message
   * on screen always describes *this* action.
   */
  const run = useCallback(
    async (
      what: string,
      write: () => Promise<{ ok: boolean; message?: string }>,
    ): Promise<boolean> => {
      setNotice(null);
      setBusy(true);
      try {
        const result = await write();
        if (!result.ok) {
          setNotice(result.message ?? 'That could not be saved.');
          return false;
        }
        reload();
        return true;
      } catch (cause: unknown) {
        log.error(MODULE, `${what} failed`, {
          matchId,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        setNotice('That could not be saved. Nothing was changed.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [matchId, reload],
  );

  const dismiss = useCallback(
    (observationId: string): Promise<boolean> =>
      run('FLAG-3: the dismissal', () => dismissFlag(matchId, observationId)),
    [matchId, run],
  );

  const attach = useCallback(
    async (observationId: string, hypothesisId: string, text: string): Promise<boolean> => {
      const done = await run('FLAG-3: the attach', () =>
        attachFlag(matchId, observationId, hypothesisId),
      );
      if (done) setJustPromoted({ id: hypothesisId, text });
      return done;
    },
    [matchId, run],
  );

  /**
   * §10.1 (b) — a slot-free library entry, which supplies real text *and* a `discoveryBucket`.
   *
   * The id is minted **here**, before the write, so the board offer below names the pattern this tap
   * created rather than re-deriving "the newest one" from the reloaded list
   * (`capture-spawning-instance-handle-at-creation`). Two flags promoted in quick succession would
   * make that re-derivation point at the wrong row.
   */
  const promoteFromTemplate = useCallback(
    async (observationId: string, templateId: string, entry: TemplateEntry): Promise<boolean> => {
      const id = newId();
      const candidate = candidateFromTemplateEntry(id, templateId, entry, entry.text, null);
      const done = await run('FLAG-3: the template promotion', () =>
        promoteFlag(matchId, observationId, candidate),
      );
      if (done) setJustPromoted({ id, text: entry.text });
      return done;
    },
    [matchId, run],
  );

  /** §10.1 (c) — the auto-label path, which needs neither a pattern nor a template to exist. */
  const promoteWithAutoLabel = useCallback(
    async (
      observationId: string,
      matchMinute: number,
      hypothesisPhase: HypothesisPhase,
    ): Promise<boolean> => {
      const id = newId();
      const text = autoLabel(matchMinute);
      const candidate = candidateFromFreeText(id, text, hypothesisPhase);
      const done = await run('FLAG-3: the auto-label promotion', () =>
        promoteFlag(matchId, observationId, candidate),
      );
      if (done) setJustPromoted({ id, text });
      return done;
    },
    [matchId, run],
  );

  const state: FlagsState = { phase, context, notice, justPromoted, busy };
  const actions: FlagsActions = {
    reload,
    dismiss,
    attach,
    promoteFromTemplate,
    promoteWithAutoLabel,
    clearPromoted: useCallback(() => {
      setJustPromoted(null);
    }, []),
    dismissNotice: useCallback(() => {
      setNotice(null);
    }, []),
  };

  return { state, actions };
}
